import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, InputNumber, Message, Modal, Select, Spin, Tabs } from '@arco-design/web-react';
import { Category, RuleVersion, ScoreConfig, scoringApi } from './api';
import './index.scss';

const categories: { key: Category; label: string }[] = [
  { key: 'NEWS', label: '国内资讯＋港澳台' },
  { key: 'JIUYANG', label: '韭研公社' }
];
const fieldLabels: Record<string, string> = {
  AUTHOR: '作者精确匹配',
  TITLE_FIRST_LINE: '标题或正文首个非空行',
  TITLE_BODY: '标题或正文'
};
const calculated = new Set(['PLATFORMS', 'THEME_CHANGE', 'THEME_RESISTANCE']);
const explainError = (e: unknown) => {
  const error = e as { message?: string; response?: { data?: { message?: string } } };
  return error.response?.data?.message || error.message || '请求失败，请重试';
};
export default function ScoringRules({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const [category, setCategory] = useState<Category>('NEWS');
  const [versions, setVersions] = useState<Partial<Record<Category, RuleVersion>>>({});
  const [drafts, setDrafts] = useState<Partial<Record<Category, ScoreConfig>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    if (!visible) return;
    setLoading(true);
    setError('');
    Promise.all(categories.map(c => scoringApi.get(c.key)))
      .then(values => {
        if (current !== generation.current) return;
        const loaded: Partial<Record<Category, RuleVersion>> = {};
        const configs: Partial<Record<Category, ScoreConfig>> = {};
        values.forEach(v => {
          loaded[v.category] = v;
          configs[v.category] = v.config;
        });
        setVersions(loaded);
        setDrafts(configs);
      })
      .catch(e => {
        if (current === generation.current) setError(explainError(e));
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
    return () => {
      generation.current++;
    };
  }, [visible]);
  const version = versions[category];
  const config = drafts[category];
  const disabled = saving || !version?.canWrite;
  const dirty = categories.some(
    c => versions[c.key] && JSON.stringify(drafts[c.key]) !== JSON.stringify(versions[c.key]?.config)
  );
  const change = (update: (c: ScoreConfig) => ScoreConfig) =>
    setDrafts(old => ({ ...old, [category]: old[category] ? update(old[category]!) : undefined }));
  const close = () => {
    if (saving) return;
    if (dirty) Modal.confirm({ title: '放弃未保存的修改？', content: '两个分类的未保存修改都会丢弃。', onOk: onClose });
    else onClose();
  };
  const save = async () => {
    if (!version || !config || disabled) return;
    setSaving(true);
    setError('');
    try {
      const updated = await scoringApi.save(category, version.version, {
        ...config,
        rules: config.rules.map(r => ({ ...r, terms: Array.from(new Set(r.terms.map(t => t.trim()).filter(Boolean))) }))
      });
      setVersions(old => ({ ...old, [category]: updated }));
      setDrafts(old => ({ ...old, [category]: updated.config }));
      Message.success('已保存，仅对后续新消息生效');
    } catch (e) {
      setError(explainError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      title="新闻评分规则"
      visible={visible}
      onCancel={close}
      footer={null}
      style={{ width: 960, maxWidth: '95vw' }}
      unmountOnExit
    >
      <div className="news-scoring-rules">
        <p>每项命中多个关键词只计一次；不同项累加。规则修改仅影响后续新消息，保留历史评分和所用版本。</p>
        <Tabs
          activeTab={category}
          onChange={key => {
            if (!saving) {
              setCategory(key as Category);
              setError('');
            }
          }}
        >
          {categories.map(c => (
            <Tabs.TabPane key={c.key} title={c.label} />
          ))}
        </Tabs>
        {error && (
          <p role="alert" className="scoring-error">
            {error}
          </p>
        )}
        {loading ? (
          <Spin />
        ) : config && version ? (
          <>
            <p>
              当前版本：{version.version} · 修改人：{version.actor} · {version.createdAt}
            </p>
            <div className="score-thresholds">
              <label>
                展示分数 ≥{' '}
                <InputNumber
                  aria-label="展示门槛"
                  precision={2}
                  value={config.displayThreshold}
                  disabled={disabled}
                  onChange={value => change(c => ({ ...c, displayThreshold: value }))}
                />
              </label>
              <label>
                精选分数{' '}
                <Select
                  aria-label="精选比较方式"
                  value={config.featuredExclusive ? 'gt' : 'gte'}
                  disabled={disabled}
                  onChange={value => change(c => ({ ...c, featuredExclusive: value === 'gt' }))}
                  style={{ width: 85 }}
                >
                  <Select.Option value="gte">≥</Select.Option>
                  <Select.Option value="gt">&gt;</Select.Option>
                </Select>
                <InputNumber
                  aria-label="精选门槛"
                  precision={2}
                  value={config.featuredThreshold}
                  disabled={disabled}
                  onChange={value => change(c => ({ ...c, featuredThreshold: value }))}
                />
              </label>
              {category === 'NEWS' && (
                <label>
                  跨平台统计自然日数{' '}
                  <InputNumber
                    aria-label="跨平台统计天数"
                    min={1}
                    max={7}
                    precision={0}
                    value={config.windowDays}
                    disabled={disabled}
                    onChange={value => change(c => ({ ...c, windowDays: value }))}
                  />
                </label>
              )}
            </div>
            <label>
              题材关联度门槛（%）
              <InputNumber
                aria-label="题材关联度门槛"
                min={0}
                max={100}
                precision={0}
                value={config.themeRelevanceMin}
                disabled={disabled}
                onChange={value => change(c => ({ ...c, themeRelevanceMin: value }))}
              />
            </label>
            {(['rankTiers', 'resistanceRankTiers'] as const).map(tierKey => (
              <section key={tierKey}>
                <h3>{tierKey === 'rankTiers' ? '题材涨幅' : '题材上涨阻力'}排名阶梯</h3>
                {config[tierKey].map((tier, i) => (
                  <div className="score-tier" key={i}>
                    <span>{i ? `第 ${config[tierKey][i - 1].maxRank + 1} 名至` : '第 1 名至'}</span>
                    <InputNumber
                      aria-label={`${tierKey}排名上限${i + 1}`}
                      min={1}
                      max={10000}
                      precision={0}
                      value={tier.maxRank}
                      disabled={disabled}
                      onChange={value =>
                        change(c => ({
                          ...c,
                          [tierKey]: c[tierKey].map((t, n) => (n === i ? { ...t, maxRank: value } : t))
                        }))
                      }
                    />
                    <span>名，加</span>
                    <InputNumber
                      aria-label={`${tierKey}排名分值${i + 1}`}
                      precision={2}
                      value={tier.points}
                      disabled={disabled}
                      onChange={value =>
                        change(c => ({
                          ...c,
                          [tierKey]: c[tierKey].map((t, n) => (n === i ? { ...t, points: value } : t))
                        }))
                      }
                    />
                    <span>分</span>
                  </div>
                ))}
              </section>
            ))}
            {category === 'NEWS' && (
              <>
                <h3>同事件跨平台阶梯（同一平台去重）</h3>
                {config.platformTiers.map((tier, i) => (
                  <div className="score-tier" key={i}>
                    <span>至少</span>
                    <InputNumber
                      aria-label={`平台数量${i + 1}`}
                      min={2}
                      max={100}
                      precision={0}
                      value={tier.minPlatforms}
                      disabled={disabled}
                      onChange={value =>
                        change(c => ({
                          ...c,
                          platformTiers: c.platformTiers.map((t, n) => (n === i ? { ...t, minPlatforms: value } : t))
                        }))
                      }
                    />
                    <span>个平台，加</span>
                    <InputNumber
                      aria-label={`平台分值${i + 1}`}
                      precision={2}
                      value={tier.points}
                      disabled={disabled}
                      onChange={value =>
                        change(c => ({
                          ...c,
                          platformTiers: c.platformTiers.map((t, n) => (n === i ? { ...t, points: value } : t))
                        }))
                      }
                    />
                    <span>分{i === config.platformTiers.length - 1 ? '（封顶）' : ''}</span>
                  </div>
                ))}
              </>
            )}
            <h3>各项规则</h3>
            {config.rules.map((rule, i) => (
              <section className="score-rule" key={rule.key}>
                <div className="score-rule-heading">
                  <strong>{rule.label}</strong>
                  {calculated.has(rule.key) ? (
                    <span>按上方阶梯计算</span>
                  ) : (
                    <label>
                      命中分值{' '}
                      <InputNumber
                        aria-label={`${rule.label}分值`}
                        min={-1000}
                        max={1000}
                        precision={2}
                        value={rule.points}
                        disabled={disabled}
                        onChange={value =>
                          change(c => ({ ...c, rules: c.rules.map((r, n) => (n === i ? { ...r, points: value } : r)) }))
                        }
                      />
                    </label>
                  )}
                </div>
                {!calculated.has(rule.key) && rule.key !== 'CLS_RED' && (
                  <>
                    <p>
                      {fieldLabels[rule.field]}；每行一项。
                      {rule.key === 'MEMO' ? '“XX团队”“【机构+行业】”表示纪要格式。' : ''}
                    </p>
                    <Input.TextArea
                      aria-label={`${rule.label}关键词`}
                      value={rule.terms.join('\n')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      disabled={disabled}
                      onChange={value =>
                        change(c => ({
                          ...c,
                          rules: c.rules.map((r, n) => (n === i ? { ...r, terms: value.split('\n') } : r))
                        }))
                      }
                    />
                  </>
                )}
              </section>
            ))}
            <div className="score-actions">
              <Button onClick={close} disabled={saving}>
                关闭
              </Button>
              <Button type="primary" onClick={save} loading={saving} disabled={!version.canWrite}>
                保存当前分类
              </Button>
            </div>
          </>
        ) : (
          !loading && <p>评分规则未加载，请关闭后重试。</p>
        )}
      </div>
    </Modal>
  );
}
